/**
 * #1185 · Los casos de prueba de una herramienta de tarifas, sin pantalla.
 *
 * Un caso es lo que Dirección sabe que tiene que salir: unos datos de entrada —los mismos que se
 * teclean en la calculadora— y los minutos e importes esperados, sacados del Excel o de una oferta
 * real. Viven con la definición, en `casosPrueba` de primer nivel del documento de
 * `tarifa_herramientas`, y no dentro de `config`: el calculador entrega el `config` entero a las
 * fórmulas como `params`, y un caso no es configuración de nada.
 *
 *   { nombre: 'Pública, 3 cuadros, 40 km',
 *     form: { srv: 'publica', C: 3, km: 40 },
 *     esperado: { tpc: 96, tpi: 12, p1: 186.82, p2: 126.9 } }
 *
 * Cada valor esperado es opcional: lo que no se rellena no se comprueba. Se calcula con
 * `tarifa-motor.js` (#1184), que es exactamente el cálculo de la calculadora, así que lo que aquí
 * sale es lo que saldría en la oferta con esa definición y esa configuración global.
 *
 * Lo que este módulo NO hace, a propósito: no decide si un cambio es bueno. Un caso que se mueve
 * puede ser justo lo que se quería —una subida de tarifa—, así que se dice cuál y cuánto y la
 * decisión es de la persona que guarda. Y un caso que no se ha podido calcular NO es un caso que
 * pasa: sale como error, con el motivo.
 *
 *   node scripts/prueba-tarifa-casos-prueba.js
 */
import TarifaMotor from './tarifa-motor.js';

/**
 * Los resultados que se pueden esperar de un caso, en el orden en que se enseñan. `clasificacion`
 * (AP) no tiene precio: decide una categoría, y es lo único que se compara en ese modo.
 */
const RESULTADOS = [
  { clave: 'tpc', etiqueta: 'Minutos en campo (TPC)', tipo: 'min' },
  { clave: 'tpi', etiqueta: 'Minutos en oficina (TPI)', tipo: 'min' },
  { clave: 'p1', etiqueta: 'Precio 1ª inspección', tipo: 'eur' },
  { clave: 'p2', etiqueta: 'Precio 2ª inspección', tipo: 'eur' },
  { clave: 'categoria', etiqueta: 'Categoría', tipo: 'texto' }
];

/**
 * Los valores con los que la calculadora rellena lo que la configuración global no trae. Son los
 * de `CFG_POR_DEFECTO` en `tarifa-calculador-v3.vue`, y tienen que seguir siéndolo: si el caso se
 * calculara con otro relleno, daría un importe que la calculadora no da. El banco lo comprueba
 * leyendo la pantalla.
 */
const CFG_POR_DEFECTO = { ph: 107, kmcost: 0.29, kmmin: 60, tdf: 0.65, com: 14, cen: 14, hot: 60, des: 4, pctFS: 30, pctNocturno: 35 };

/**
 * La configuración global con la que se calculan los casos, a partir de lo que devuelve
 * `Tarifa.getConfig`, o `null` si no se puede calcular con ella.
 *
 * La misma regla que `cargarConfig` + `normalizarConfig` de la calculadora: los números que llegan
 * como texto pasan a número y lo que falte se rellena con `CFG_POR_DEFECTO`. Con una diferencia
 * deliberada: la calculadora, si la respuesta no trae `ph`, se queda calculando con el relleno; aquí
 * eso es «no se ha podido leer la configuración» y los casos no se comprueban. Un aviso de «todo
 * cuadra» calculado con un precio hora de relleno sería una afirmación falsa.
 */
function configGlobal(respuesta) {
  if (!respuesta || !respuesta.ph) return null;
  const p = {};
  Object.keys(respuesta).forEach(function (k) {
    const v = respuesta[k];
    p[k] = typeof v === 'string' && !isNaN(v) ? parseFloat(v) : v;
  });
  return Object.assign({}, CFG_POR_DEFECTO, p);
}

/** Si la herramienta trae casos que ejecutar. Sin casos, el editor se comporta como antes. */
function tieneCasos(herramienta) {
  return !!(herramienta && Array.isArray(herramienta.casosPrueba) && herramienta.casosPrueba.length);
}

/** Si en ese valor esperado hay algo que comprobar: vacío no es cero. */
function hayValor(v) {
  return v !== null && v !== undefined && String(v).trim() !== '';
}

function redondeo2(n) {
  return Math.round(n * 100) / 100;
}

/**
 * La categoría como la enseña la calculadora: su `cat` («I», «II»…). El motor entrega la fila
 * entera de la tabla de categorías, y comparada como texto toda fila es «[object Object]», así que
 * pasar de I a II no se notaba. Acepta también un `esperado` guardado con la fila entera, que es lo
 * que dejaba «Usar el resultado actual como esperado» antes de normalizarlo aquí.
 */
function categoriaDe(v) {
  if (v && typeof v === 'object') {
    // Una fila sin `cat` se cita por lo siguiente que la distinga: hallada una categoría, nunca «—».
    const t = [v.cat, v.nombre, v.rango].filter(function (x) { return x !== null && x !== undefined && String(x).trim() !== ''; })[0];
    return t !== undefined ? String(t) : v.min + '–' + v.max;
  }
  return v === undefined ? null : v;
}

/**
 * Lo que tiene el formulario de la calculadora antes de teclear nada: `initForm` de
 * `tarifa-calculador-v3.vue`, copiado regla a regla. Un dato que el caso no trae es un dato que en la
 * calculadora nadie ha tocado, y ahí no está vacío: tiene este valor. Sin él, un caso sin km
 * calculaba con el mínimo de la configuración global y la calculadora con 60 (o el de la
 * herramienta), y un número sin teclear entraba en la fórmula como `undefined`, que la calculadora
 * no puede dar nunca. El banco ejecuta el `initForm` de la pantalla y comprueba que dan lo mismo.
 */
function formInicial(herramienta) {
  const h = herramienta || {};
  const f = { mismo: false, km: 60, dto: 1, dto2: 1, sub: 0, peaj: 0, ncom: 0, ncen: 0, nhot: 0, ndes: 0, sub2: 0, peaj2: 0, ncom2: 0, ncen2: 0, nhot2: 0, ndes2: 0, recargoFS: false, recargoNocturno: false };
  if (h.config && h.config.kmmin) f.km = h.config.kmmin;
  (Array.isArray(h.secciones) ? h.secciones : []).forEach(function (sec) {
    ((sec && sec.widgets) || []).forEach(function (w) {
      const v = valorInicialDe(w);
      if (v !== undefined) f[w.campo] = v;
      if (w && w.subInput && w.subInput.campo) f[w.subInput.campo] = w.subInput.defaultValue != null ? w.subInput.defaultValue : 0;
    });
  });
  return f;
}

/** El valor con el que la calculadora arranca un campo, o `undefined` si ese tipo no lo inicializa. */
function valorInicialDe(w) {
  if (!w || !w.campo) return undefined;
  const primera = Array.isArray(w.options) && w.options.length ? w.options[0].key : '';
  if (w.tipo === 'number') return w.defaultValue != null ? w.defaultValue : (w.min != null ? w.min : 0);
  if (w.tipo === 'checkbox' || w.tipo === 'checkboxCard') return w.defaultValue || false;
  if (['select', 'togglePair', 'radioCard', 'levelSelector'].indexOf(w.tipo) >= 0) return w.defaultValue || primera;
  if (w.tipo === 'tableCheckboxes') return w.defaultValue || {};
  return undefined;
}

/**
 * La inspección con la que calcula el caso: la suya, o —si no tiene— la primera, que es la que la
 * calculadora deja marcada al abrir la herramienta (`selectHerramienta`). Con subtipos, la
 * calculadora nunca calcula sin uno. Uno que ya no existe no llega aquí: es un error del caso.
 */
function srvDelCaso(herramienta, form) {
  const opciones = (herramienta && herramienta.subtipos && herramienta.subtipos.opciones) || {};
  const claves = Object.keys(opciones);
  const propia = form && form.srv;
  if (propia && opciones[propia]) return propia;
  return claves.length ? claves[0] : (propia || null);
}

/**
 * El subtipo del caso si la definición tiene subtipos y ese ya no es uno de ellos —se ha borrado o
 * se le ha cambiado la clave—, o `null`. La calculadora, con una línea así, cae en el primero sin
 * decir nada; un caso no puede: calcularía otra inspección y daría por bueno o por malo algo que ya
 * no es lo que el caso describe.
 */
function subtipoQueYaNoEsta(herramienta, form) {
  const opciones = (herramienta && herramienta.subtipos && herramienta.subtipos.opciones) || {};
  const propia = form && form.srv;
  return propia && Object.keys(opciones).length && !opciones[propia] ? String(propia) : null;
}

/**
 * Los minutos que pone el subtipo en una herramienta de modo manual (SMEX), como `setSrv` de la
 * calculadora al elegirlo: `params.tpcDefault` y `params.tpiDefault`. Sin esto, un caso que solo
 * elegía el subtipo calculaba con 0 y 0, «Usar el resultado actual como esperado» aceptaba un precio
 * que la calculadora no da, y cambiar los minutos del subtipo no avisaba al guardar.
 *
 * Lo tecleado en el caso manda, incluido un 0: es la persona corrigiendo el valor del subtipo, igual
 * que en la calculadora después de elegirlo.
 */
function minutosDelSubtipo(herramienta, srv) {
  const h = herramienta || {};
  if (!(h.config && h.config.modoCalculo === 'manual')) return {};
  const op = h.subtipos && h.subtipos.opciones && h.subtipos.opciones[srv];
  const params = (op && op.params) || {};
  const salida = {};
  if (params.tpcDefault !== undefined) salida.tpc = params.tpcDefault;
  if (params.tpiDefault !== undefined) salida.tpi = params.tpiDefault;
  return salida;
}

/**
 * Los datos del caso como los recibe el motor desde la calculadora: lo que la calculadora tiene sin
 * tocar (`formInicial`), los minutos del subtipo si es manual, y encima lo que el caso trae. Una
 * tabla de casillas que no sea un objeto se deja como la calculadora la tiene siempre —un objeto,
 * vacío si no hay ninguna fila marcada—, porque una fórmula que lea `form.sis.det` revienta sin él.
 */
function formDelCaso(herramienta, form) {
  const propio = form || {};
  const srv = srvDelCaso(herramienta, propio);
  const f = Object.assign(formInicial(herramienta), srv ? { srv: srv } : {});
  const delSubtipo = minutosDelSubtipo(herramienta, srv);
  Object.keys(delSubtipo).forEach(function (k) { f[k] = delSubtipo[k]; });
  Object.keys(propio).forEach(function (k) {
    if (k !== 'srv' && hayDato(propio[k])) f[k] = propio[k];
  });
  ((herramienta && herramienta.secciones) || []).forEach(function (sec) {
    ((sec && sec.widgets) || []).forEach(function (w) {
      if (w && w.tipo === 'tableCheckboxes' && w.campo && (!f[w.campo] || typeof f[w.campo] !== 'object')) f[w.campo] = {};
    });
  });
  return f;
}

/**
 * Si un dato del caso dice algo. Un dato del caso tiene dos estados y solo dos: «sin teclear», que
 * calcula con lo que la calculadora tiene sin tocar, y «puesto», que calcula con lo puesto. Sin
 * teclear es solo la caja vacía —`''`, `null`, un número que no es número—. Un `0`, una casilla
 * desmarcada (`false`) o una tabla sin filas (`{}`) son datos puestos: son justo lo que se teclea para
 * quitar lo que la herramienta trae por defecto, y si contaran como vacíos el cálculo volvería al
 * valor por defecto mientras la pestaña enseña otra cosa.
 *
 * Esta regla es la única: la aplica `formDelCaso` al calcular y `conDato` al escribir, y la pestaña
 * no decide por su cuenta qué guarda ni qué quita.
 */
function hayDato(v) {
  if (v === null || v === undefined) return false;
  if (typeof v === 'number') return isFinite(v);
  if (typeof v === 'string') return v.trim() !== '';
  return true;
}

/**
 * Lo que vale un dato del caso que se ha dejado sin teclear, para enseñarlo en su caja: así la
 * pestaña no dice «vacío» mientras el cálculo usa otra cosa. `undefined` si no hay nada que decir.
 */
function porDefecto(herramienta, form, campo) {
  const f = formDelCaso(herramienta, Object.assign({}, form || {}, { [campo]: undefined }));
  return f[campo];
}

/**
 * Lo que el cálculo usa para ese dato: lo puesto en el caso o, si no hay, lo de la calculadora sin
 * tocar. Es lo que tiene que enseñar cada control de la pestaña —una casilla, las filas de una
 * tabla—: si leyeran solo lo guardado en el caso, una fila marcada por defecto saldría desmarcada
 * mientras el caso calcula con ella.
 */
function valorDelCaso(herramienta, form, campo) {
  return formDelCaso(herramienta, form)[campo];
}

/**
 * Los datos del caso con ese dato escrito, en una copia. Vacío (`hayDato`) lo quita y el caso vuelve
 * a calcular con el valor por defecto, que es lo que la caja vacía enseña; cualquier otra cosa se
 * guarda tal cual, también `0`, `false` y `{}`.
 */
function conDato(form, campo, valor) {
  const salida = Object.assign({}, form || {});
  if (hayDato(valor)) salida[campo] = valor;
  else delete salida[campo];
  return salida;
}

/**
 * Los datos del caso con una fila de una tabla de casillas marcada o desmarcada, con la forma que le
 * da la calculadora (`toggleTableRow`): un objeto con las filas marcadas. Se parte de lo que el
 * cálculo usa —las filas por defecto si el caso no ha tocado la tabla—, que es lo que se ve marcado;
 * y desmarcar la última deja la tabla vacía, no «sin tocar», porque sin tocar son las de por defecto.
 */
function conFila(herramienta, form, campo, fila, marcada) {
  const actual = valorDelCaso(herramienta, form, campo);
  const nueva = Object.assign({}, actual && typeof actual === 'object' ? actual : {});
  if (marcada) nueva[fila] = true;
  else delete nueva[fila];
  return conDato(form, campo, nueva);
}

/** Lo que el motor ha dado, con la misma forma que `esperado`. */
function obtenidoDe(x) {
  const r = (x && x.res) || {};
  if (r.tipo === 'clasificacion') {
    return { categoria: categoriaDe(r.categoria) };
  }
  return {
    tpc: typeof r.tpc === 'number' ? r.tpc : null,
    tpi: typeof r.tpi === 'number' ? r.tpi : null,
    p1: typeof r._p1 === 'number' ? redondeo2(r._p1) : null,
    p2: typeof r._p2 === 'number' ? redondeo2(r._p2) : null
  };
}

/**
 * Si lo obtenido coincide con lo esperado. Los minutos se comparan como los enseña la calculadora
 * —redondeados a minuto, que es lo que `res` trae— y los importes al céntimo, que es lo que va en
 * la oferta: medio céntimo de holgura para que una coma flotante no cuente como un cambio.
 */
function coincide(resultado, esperado, obtenido) {
  if (obtenido === null || obtenido === undefined) return false;
  if (resultado.tipo === 'texto') return String(categoriaDe(esperado)).trim() === String(obtenido).trim();
  const e = Number(String(esperado).replace(',', '.'));
  if (!isFinite(e)) return false;
  if (resultado.tipo === 'eur') return Math.abs(e - obtenido) < 0.005;
  return Math.round(e) === obtenido;
}

/**
 * Ejecuta un caso contra una definición. Devuelve:
 *
 *  - `obtenido`: lo que da la definición, con la forma de `esperado`;
 *  - `diferencias`: cada resultado esperado que no coincide, con los dos valores;
 *  - `comprobados`: cuántos valores esperados se han mirado. Un caso sin ninguno no dice nada;
 *  - `error`: el motivo si no se ha podido calcular. Entonces no hay `obtenido`, y el caso NO pasa.
 */
function ejecutarCaso(herramienta, caso, cfg) {
  const c = caso || {};
  const esperado = c.esperado || {};
  const salida = { caso: c, obtenido: null, diferencias: [], comprobados: 0, error: null };
  if (!cfg) {
    salida.error = 'no se ha podido leer la Configuración global de tarifas';
    return salida;
  }
  const perdido = subtipoQueYaNoEsta(herramienta, c.form);
  if (perdido) {
    salida.error = 'el subtipo «' + perdido + '» ya no existe en la definición';
    return salida;
  }
  let x;
  try {
    x = TarifaMotor.calcular({ herramienta: herramienta, form: formDelCaso(herramienta, c.form), cfg: cfg });
  } catch (e) {
    salida.error = 'la definición no se puede calcular con estos datos (' + ((e && e.message) || e) + ')';
    return salida;
  }
  if (!x) {
    salida.error = 'no hay herramienta que calcular';
    return salida;
  }
  salida.obtenido = obtenidoDe(x);
  RESULTADOS.forEach(function (resultado) {
    const esp = esperado[resultado.clave];
    if (!hayValor(esp)) return;
    salida.comprobados++;
    const obt = salida.obtenido[resultado.clave];
    if (!coincide(resultado, esp, obt)) {
      salida.diferencias.push({ resultado: resultado, esperado: esp, obtenido: obt === undefined ? null : obt });
    }
  });
  return salida;
}

/**
 * Todos los casos de la definición, en su orden. Cada resultado lleva su `indice` en la lista: el
 * aviso los cita por nombre, y uno sin nombre se cita por su posición, que es la de la pestaña.
 */
function ejecutarCasos(herramienta, cfg) {
  if (!tieneCasos(herramienta)) return [];
  return herramienta.casosPrueba.map(function (caso, i) {
    return Object.assign(ejecutarCaso(herramienta, caso, cfg), { indice: i });
  });
}

/** Los que hay que contar antes de publicar: los que se mueven y los que no se han podido calcular. */
function cambios(resultados) {
  return (resultados || []).filter(function (r) { return r.error || r.diferencias.length; });
}

/** Un valor como lo lee una persona: «186,82 €», «96 min», o «—» si no hay. */
function formatear(resultado, valor) {
  if (resultado.tipo === 'texto') valor = categoriaDe(valor);
  if (valor === null || valor === undefined || valor === '') return '—';
  if (resultado.tipo === 'texto') return String(valor);
  const n = Number(String(valor).replace(',', '.'));
  if (!isFinite(n)) return String(valor);
  return resultado.tipo === 'eur' ? TarifaMotor.fmtEur(n) : Math.round(n) + ' min';
}

/** Cuánto se ha movido, con signo: «+3,28 €», «−4 min». Vacío si no se puede restar. */
function cuanto(diferencia) {
  const r = diferencia.resultado;
  if (r.tipo === 'texto' || diferencia.obtenido === null) return '';
  const delta = Number(diferencia.obtenido) - Number(String(diferencia.esperado).replace(',', '.'));
  if (!isFinite(delta)) return '';
  const signo = delta > 0 ? '+' : '−';
  return signo + formatear(r, Math.abs(delta));
}

/** El nombre con el que se cita un caso: el suyo, o su posición si no tiene. */
function nombreDe(resultado, i) {
  const n = resultado.caso && resultado.caso.nombre;
  const posicion = typeof i === 'number' ? i : resultado.indice;
  return n && String(n).trim() ? String(n).trim() : 'Caso ' + ((posicion || 0) + 1);
}

/**
 * Lo que hay que poner en «esperado» para que el caso dé por bueno lo que sale ahora. Es el botón
 * «Usar el resultado actual como esperado»: un cambio de tarifa que se quería mover el precio se
 * acepta así, a mano y caso por caso, y no aceptando en bloque desde el aviso del guardado.
 */
function esperadoDesde(obtenido) {
  const salida = {};
  Object.keys(obtenido || {}).forEach(function (k) {
    if (obtenido[k] !== null && obtenido[k] !== undefined) salida[k] = obtenido[k];
  });
  return salida;
}

const TarifaCasosPrueba = {
  RESULTADOS: RESULTADOS,
  CFG_POR_DEFECTO: CFG_POR_DEFECTO,
  configGlobal: configGlobal,
  formInicial: formInicial,
  formDelCaso: formDelCaso,
  srvDelCaso: srvDelCaso,
  porDefecto: porDefecto,
  valorDelCaso: valorDelCaso,
  conDato: conDato,
  conFila: conFila,
  tieneCasos: tieneCasos,
  ejecutarCaso: ejecutarCaso,
  ejecutarCasos: ejecutarCasos,
  cambios: cambios,
  formatear: formatear,
  cuanto: cuanto,
  nombreDe: nombreDe,
  esperadoDesde: esperadoDesde
};

export default TarifaCasosPrueba;
